// @flow

import React, { useState, useCallback, useLayoutEffect, type Node } from 'react';
import styled from 'styled-components';
import { useIncomingPayment, isRetry } from '@tectonics/prs/incoming-payment';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';

import { hasUserInputValidationError, hasSubmitError, useValidation } from '@tectonics/prs/validation';

import { isElementVisible } from '@tectonics/look-and-feel/commons/dom-utils';
import { scrollToTop, scrollToFirstFieldWithError } from '@tectonics/screen';
import { useFormStatusContext } from '@tectonics/prs/form-status/use-form-status-context';
import { useSubmitting } from '@tectonics/prs/submitting';

import { Padding, HighlightBox } from '../../../commons/atoms';
import { Header } from '../header';

import { PaymentMethodFields } from '../payment-method';
import { PlaceOrderButton } from '../place-order-button';
import { CustomerFields } from '../customer';
import { CaptchaFields } from '../captcha';

import { SubmitErrorDisplay } from './submit-error-display';
import { InfoMessage } from './info-message';

type Props = {|
  onSubmit: (boolean, boolean) => Promise<boolean>,
|};

const FormElement = styled.form`
  display: grid;
  grid-gap: 16px;
`;

export function Form({ onSubmit }: Props): Node {
  const formHasBeenSent = useFormHasBeenSent();
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const incomingPayment = useIncomingPayment();
  const submitting = useSubmitting();
  const retry = isRetry(incomingPayment);

  const [, setFormStatus] = useFormStatusContext();
  const handleSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>) => {
      event.preventDefault();
      (async () => {
        const hasErrors = Array.from(document.querySelectorAll('[data-error]')).filter(isElementVisible).length > 0;

        setFormStatus('sent');
        setHasScrolled(false);
        await onSubmit(formHasBeenSent, hasErrors);
      })();
    },
    [onSubmit, setFormStatus, formHasBeenSent]
  );

  const validation = useValidation();

  useLayoutEffect(() => {
    if (!hasScrolled && hasUserInputValidationError(validation)) {
      scrollToFirstFieldWithError();
      setHasScrolled(true);
    }
  }, [hasScrolled, setHasScrolled, validation]);

  useLayoutEffect(() => {
    const shouldScrollToTop = !hasScrolled && !submitting && (hasSubmitError(validation) || retry);

    if (shouldScrollToTop) {
      scrollToTop();
      setHasScrolled(true);
    }
  }, [hasScrolled, setHasScrolled, validation, retry, submitting]);

  return (
    <FormElement onSubmit={handleSubmit}>
      <Header />

      <SubmitErrorDisplay />

      <HighlightBox bordered>
        <Padding value="1em">
          <CustomerFields />
          <PaymentMethodFields />
        </Padding>
      </HighlightBox>
      <InfoMessage />

      <CaptchaFields />

      <PlaceOrderButton />
    </FormElement>
  );
}
